// Dibujo del mundo de la oficina (S11). Todo en rectángulos de píxel entero sobre «píxeles de arte» (la
// escena amplía por un zoom entero: nítido). Reutiliza el arte de lib/oficina/pixel-art (tarima, mesa,
// silla, planta, lámpara) y añade lo que pide una planta abierta: ventanales, zócalo con puerta, zonas
// con alfombra, sala de reuniones, sofás, cocina, recepción y estanterías.
import { alfombra, lampara, mesa, planta, silla, PAL } from '../../../lib/oficina/pixel-art';
import { CELDA, PARED, ZOCALO, type DecorMundo, type Mundo, type Rect } from './generar';

type Ctx = CanvasRenderingContext2D;

const C = {
  pared: '#161618',
  paredLuz: '#8A6D1C',
  paredSombra: '#0E0E10',
  cielo: ['#1B2430', '#212C3A', '#263344'],
  marco: '#2A2A2E',
  zocalo: '#121214',
  puerta: '#2A2A2E',
  felpudo: '#3A2F0B',
  sofa: '#2C2C31',
  sofaLuz: '#3A3A40',
  sofaSombra: '#1E1E22',
  cojin: '#B88A06',
  mesaBaja: '#2E2E33',
  encimera: '#3A3A40',
  encimeraSombra: '#2A2A2E',
  armario: '#232327',
  nevera: '#8F8F96',
  mostrador: '#1E1E22',
  // Suelo (crítica C2): madera oscura templada y apagada; acompaña, no compite con personas ni interfaz.
  suelo: ['#1C1C1F', '#1E1E21', '#1A1A1D', '#1D1D20'],
  sueloJunta: '#141416',
  sueloVeta: '#232327',
} as const;

const r = (ctx: Ctx, x: number, y: number, w: number, h: number, c: string) => {
  ctx.fillStyle = c;
  ctx.fillRect(Math.round(x), Math.round(y), Math.round(w), Math.round(h));
};

function hash(n: number): number {
  let h = (n * 374761393) >>> 0;
  h = (h ^ (h >>> 13)) >>> 0;
  return Math.imul(h, 1274126177) >>> 0;
}

/** Pared superior con ventanales (luz de día que entra al suelo). */
function paredConVentanales(ctx: Ctx, ancho: number): void {
  r(ctx, 0, 0, ancho, PARED, C.pared);
  r(ctx, 0, PARED - 6, ancho, 6, C.paredSombra);
  r(ctx, 0, PARED - 7, ancho, 1, C.paredLuz);
  const w = 84;
  const hueco = 28;
  for (let x = 24; x + w < ancho - 12; x += w + hueco) {
    r(ctx, x - 2, 8, w + 4, 26, C.marco);
    for (let i = 0; i < 3; i += 1) r(ctx, x + (i * w) / 3 + 1, 10, w / 3 - 2, 22, C.cielo[i % 3]!);
    r(ctx, x, 10, w, 1, '#8A6D1C');
    // luz de la ventana sobre el suelo
    const g = ctx.createLinearGradient(0, PARED, 0, PARED + 70);
    g.addColorStop(0, 'rgba(240,185,11,0.06)');
    g.addColorStop(1, 'rgba(240,185,11,0)');
    ctx.fillStyle = g;
    ctx.fillRect(x - 6, PARED, w + 12, 70);
  }
}

function zocaloConPuerta(ctx: Ctx, m: Mundo): void {
  const y = m.alto - ZOCALO;
  r(ctx, 0, y, m.ancho, ZOCALO, C.zocalo);
  r(ctx, 0, y, m.ancho, 1, '#0A0A0B');
  // Puerta de entrada (hueco en el zócalo) y felpudo.
  r(ctx, m.puerta.x - 20, y, 40, ZOCALO, C.puerta);
  r(ctx, m.puerta.x - 1, y + 2, 2, ZOCALO - 2, '#8A6D1C');
  r(ctx, m.puerta.x - 18, y - 12, 36, 10, C.felpudo);
  r(ctx, m.puerta.x - 16, y - 10, 32, 1, '#2A2A2E');
}

/** Alfombra de la sala: borde de dos franjas en el color de la sala (al 60 %), interior apenas más claro. */
function zonaSala(ctx: Ctx, z: { rect: Rect; color: string }): void {
  const b = z.rect;
  r(ctx, b.x, b.y, b.w, b.h, '#232327');
  // Un solo filete fino del color de la sala (crítica: el doble borde dorado competía con las personas).
  ctx.fillStyle = z.color;
  ctx.globalAlpha = 0.4;
  for (const g of [0]) {
    ctx.fillRect(b.x + g, b.y + g, b.w - 2 * g, 2);
    ctx.fillRect(b.x + g, b.y + b.h - g - 2, b.w - 2 * g, 2);
    ctx.fillRect(b.x + g, b.y + g, 2, b.h - 2 * g);
    ctx.fillRect(b.x + b.w - g - 2, b.y + g, 2, b.h - 2 * g);
  }
  ctx.globalAlpha = 1;
}

/** Mesa vista desde el otro lado (fila de arriba de la isla): la trasera del monitor queda abajo. */
function mesaGirada(ctx: Ctx, x: number, y: number, w: number, h: number): void {
  r(ctx, x + 2, y + h, w - 4, 3, PAL.sombra);
  r(ctx, x, y, w, h, PAL.mesa);
  r(ctx, x, y, w, 2, PAL.mesaLuz);
  r(ctx, x, y + h - 3, w, 3, PAL.mesaSombra);
  r(ctx, x + Math.round(w / 2) - 9, y + 4, 18, 5, PAL.metal); // teclado, del lado de la persona
  r(ctx, x + Math.round(w / 2) - 11, y + h - 2, 22, 6, PAL.metal); // trasera del monitor
  r(ctx, x + w - 12, y + 3, 9, 7, PAL.papel);
}

/** Una isla de 4: dos puestos arriba (mesa debajo de la persona) y dos abajo (mesa encima), mesas juntas.
 *  Media isla (sala sin nadie): solo la fila de arriba. 09-10: un puesto que no está (la última isla lleva 1-3, o
 *  Pau quitó esa mesa) se queda en alfombra, sin mesa ni silla. */
function isla(ctx: Ctx, b: Rect, puestos: ReadonlyArray<{ k: number; encendida: boolean }>): void {
  const media = b.h < 2 * CELDA.h;
  alfombra(ctx, b.x + 2, b.y + 6, b.w - 4, b.h - (media ? 4 : 12), '#26262B');
  for (const { k, encendida } of puestos) {
    const arriba = k < 2;
    const cx = b.x + (k % 2) * CELDA.w + CELDA.w / 2;
    if (arriba) {
      silla(ctx, cx, b.y + 30);
      mesaGirada(ctx, cx - 28, b.y + CELDA.h - 22, 56, 20);
    } else {
      mesa(ctx, cx - 28, b.y + CELDA.h + 12, 56, 20, encendida, 0.6);
      silla(ctx, cx, b.y + 2 * CELDA.h - 22);
    }
  }
}

function reuniones(ctx: Ctx, b: Rect): void {
  alfombra(ctx, b.x, b.y, b.w, b.h, '#24242A');
  const t: Rect = { x: b.x + 24, y: b.y + b.h / 2 - 14, w: b.w - 48, h: 28 };
  // sillas alrededor
  for (let x = t.x + 6; x + 16 < t.x + t.w; x += 26) {
    r(ctx, x, t.y - 12, 16, 10, PAL.silla);
    r(ctx, x, t.y + t.h + 2, 16, 10, PAL.silla);
  }
  r(ctx, t.x + 2, t.y + t.h, t.w - 4, 3, PAL.sombra);
  r(ctx, t.x, t.y, t.w, t.h, PAL.mesa);
  r(ctx, t.x, t.y, t.w, 2, PAL.mesaLuz);
  r(ctx, t.x + 8, t.y + 8, 10, 7, PAL.papel);
  r(ctx, t.x + t.w - 22, t.y + 10, 14, 8, PAL.metal);
}

function sofas(ctx: Ctx, b: Rect): void {
  alfombra(ctx, b.x, b.y + 10, b.w, b.h - 10, '#8A6D1C');
  const sw = Math.min(b.w - 20, 96);
  const sx = b.x + (b.w - sw) / 2;
  // sofá de espaldas a la pared (arriba) y mesa baja delante
  r(ctx, sx + 2, b.y + 30, sw - 4, 3, PAL.sombra);
  r(ctx, sx, b.y + 4, sw, 26, C.sofaSombra);
  r(ctx, sx, b.y + 4, sw, 10, C.sofa);
  r(ctx, sx + 4, b.y + 14, sw - 8, 14, C.sofaLuz);
  r(ctx, sx + 10, b.y + 16, 12, 8, C.cojin);
  r(ctx, sx + sw - 22, b.y + 16, 12, 8, C.cojin);
  const mw = Math.round(sw * 0.5);
  r(ctx, sx + (sw - mw) / 2, b.y + b.h - 34, mw, 16, C.mesaBaja);
  r(ctx, sx + (sw - mw) / 2, b.y + b.h - 34, mw, 2, PAL.mesaLuz);
  r(ctx, sx + sw / 2 - 4, b.y + b.h - 30, 8, 6, '#E8E6DF');
  planta(ctx, b.x + 2, b.y + b.h - 30);
  lampara(ctx, b.x + b.w - 14, b.y + 6);
}

function cocina(ctx: Ctx, b: Rect): void {
  r(ctx, b.x, b.y, b.w, b.h, C.armario);
  r(ctx, b.x, b.y, b.w, 12, C.encimera);
  r(ctx, b.x, b.y + 11, b.w, 1, C.encimeraSombra);
  // cafetera, fregadero, nevera
  r(ctx, b.x + 10, b.y + 1, 12, 9, PAL.metal);
  r(ctx, b.x + 13, b.y + 3, 6, 3, '#F0B90B');
  r(ctx, b.x + 34, b.y + 2, 20, 8, '#9AA3AE');
  r(ctx, b.x + 36, b.y + 4, 16, 4, '#6E7884');
  r(ctx, b.x + b.w - 26, b.y - 6, 24, b.h + 6, C.nevera); // más alta que la encimera, dentro de su hueco
  r(ctx, b.x + b.w - 26, b.y + 10, 24, 1, '#9AA3AE');
  r(ctx, b.x + b.w - 6, b.y + 2, 2, 6, '#8A9098');
  for (let x = b.x + 4; x < b.x + b.w - 30; x += 18) r(ctx, x, b.y + 18, 14, 1, '#2E2E33');
}

function recepcion(ctx: Ctx, b: Rect): void {
  r(ctx, b.x + 2, b.y + b.h, b.w - 4, 3, PAL.sombra);
  r(ctx, b.x, b.y + 10, b.w, b.h - 10, C.mostrador);
  r(ctx, b.x, b.y + 10, b.w, 3, PAL.mesaLuz);
  r(ctx, b.x, b.y + b.h - 4, b.w, 4, PAL.mesaSombra);
  r(ctx, b.x + b.w / 2 - 11, b.y, 22, 11, PAL.metal);
  r(ctx, b.x + b.w / 2 - 10, b.y + 1, 20, 8, PAL.pantalla);
  r(ctx, b.x + 8, b.y + 14, 10, 6, PAL.papel);
  planta(ctx, b.x + b.w - 14, b.y + 12);
}

/** Suelo de tablas: tonos cercanos, juntas suaves y una veta cada pocas tablas (nada de rayas negras). */
function suelo(ctx: Ctx, b: Rect): void {
  const alto = 10;
  for (let y = b.y; y < b.y + b.h; y += alto) {
    let x = b.x - (hash(y) % 40);
    let k = 0;
    while (x < b.x + b.w) {
      const largo = 56 + (hash(x * 7 + y) % 48);
      const x0 = Math.max(b.x, x);
      const x1 = Math.min(b.x + b.w, x + largo);
      if (x1 > x0) {
        r(ctx, x0, y, x1 - x0, alto, C.suelo[hash(x + y * 3) % C.suelo.length]!);
        if (hash(x * 13 + y) % 4 === 0) r(ctx, x0 + 6, y + 4, Math.min(20, x1 - x0 - 12), 1, C.sueloVeta);
        if (x1 < b.x + b.w) r(ctx, x1 - 1, y, 1, alto, C.sueloJunta);
      }
      x += largo;
      k += 1;
    }
    r(ctx, b.x, y + alto - 1, b.w, 1, C.sueloJunta);
  }
}

function decor(ctx: Ctx, d: DecorMundo): void {
  if (d.tipo === 'reuniones') reuniones(ctx, d.rect);
  else if (d.tipo === 'sofas') sofas(ctx, d.rect);
  else if (d.tipo === 'cocina') cocina(ctx, d.rect);
  else if (d.tipo === 'recepcion') recepcion(ctx, d.rect);
  else planta(ctx, d.rect.x, d.rect.y);
}

/** El fondo estático del mundo (se cachea; se rehace solo si cambia el mundo). */
export function dibujarMundo(ctx: Ctx, m: Mundo, encendidas: ReadonlySet<string>): void {
  suelo(ctx, { x: 0, y: 0, w: m.ancho, h: m.alto });
  paredConVentanales(ctx, m.ancho);
  zocaloConPuerta(ctx, m);
  for (const z of m.zonas) zonaSala(ctx, z);
  for (const b of m.islas) {
    // Los puestos de la isla, en su orden (las medias islas tienen 2: no se cuentan de 4 en 4).
    const puestos = m.mesas.filter((p) => !p.hueco && p.rect.x >= b.x && p.rect.x < b.x + b.w && p.rect.y >= b.y && p.rect.y < b.y + b.h);
    isla(ctx, b, puestos.map((p) => ({ k: p.indice % 4, encendida: !!p.agentId && encendidas.has(p.agentId) })));
  }
  m.decor.forEach((d) => decor(ctx, d));
}
